var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/** @file The models for the normal file trees in the app. */
import * as api from '@codegrade/api';
import * as atQuiz from '@codegrade/autotest-quiz';
import * as deque from '@codegrade/deque';
import { Maybe } from '@codegrade/maybe';
import { assert, expr, map, object, sort } from '@codegrade/utils';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import { Glob } from './glob';
/** All the possible revisions. */
export const REVISIONS = ['student', 'teacher'];
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class AutoTestDirectoryServerData extends api.DirectoryWithChildren {
    autoTestSuiteId;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data, autoTestSuiteId) {
        super(data);
        this.autoTestSuiteId = autoTestSuiteId;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getDepthAtrr(f) {
    return f.depth;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getDepth(f) {
    return f.parent.map(getDepthAtrr).orDefault(0) + 1;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class FileWithParentAndName {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getFullNameParts() {
        const parts = [this.name];
        let parent = this.parent;
        while (parent.isJust()) {
            parts.push(parent.extract().name);
            parent = parent.extract().parent;
        }
        // We don't want the top level directory as this never has a meaningful
        // name.
        parts.pop();
        return parts.reverse();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getFullName() {
        return this.getFullNameParts().join('/');
    }
    /** @returns The name to display in the UI. */
    get displayName() {
        return atQuiz.cleanupQuizName(this.name);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class BaseFile extends FileWithParentAndName {
    id;
    name;
    parent;
    hash;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, name, parent, hash) {
        super();
        this.id = id;
        this.name = name;
        this.parent = parent;
        this.hash = hash;
    }
    get depth() {
        return getDepth(this);
    }
    /** @returns The path to this file relative to the root of the file tree. */
    get path() {
        return this.parent.caseOf({
            Nothing: () => [this.name],
            Just: p => p.path.concat(this.name),
        });
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], BaseFile.prototype, "depth", null);
__decorate([
    CacheDecorator.cache
], BaseFile.prototype, "path", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class File extends BaseFile {
    id;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isDir = false;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, name, parent, hash) {
        super(id, name, parent, hash);
        this.id = id;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(serverData, parent) {
        return new File(serverData.id, serverData.name, Maybe.fromNullable(parent), serverData.hash);
    }
    /**
     * Create a file from community library item data.
     *
     * @param data The data to use.
     * @param parent The parent of this file.
     * @returns A fresh file.
     */
    static fromCommunityLibraryData(data, parent) {
        return new File(data.id, data.name, Maybe.of(parent), Maybe.empty());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromExtractTree(extractTree, parent) {
        return new File(null, extractTree.name, Maybe.fromNullable(parent), Maybe.empty());
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Directory extends BaseFile {
    id;
    /** The cache used in this class. */
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isDir = true;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _entries;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, name, parent) {
        super(id, name, parent, Maybe.empty());
        this.id = id;
        this._entries = [];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _setEntries(entries) {
        this._entries = Object.freeze(entries);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get entries() {
        return this._entries;
    }
    /**
     * Get a lookup mapping between filename and file/directory in this tree.
     *
     * Note that it might be possible for directory to contain multiple files
     * with the same name. It is undefined which file will be mapped in such
     * cases.
     *
     * @returns A mapping as described above.
     */
    get entryLookup() {
        return new map.ReadonlyMaybeMap(this._entries.map(entry => [entry.name, entry]));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(serverData, parent, dirCls = Directory, fileCls = File) {
        // eslint-disable-next-line new-cap
        const self = new dirCls(serverData.id, serverData.name, Maybe.fromNullable(parent));
        const entries = serverData.entries.map(entry => {
            switch (entry.type) {
                case 'file':
                    return fileCls.fromServerData(entry, self);
                case 'directory':
                    return dirCls.fromServerData(entry, self, Directory);
                default:
                    return assert.AssertionError.assertNever(entry);
            }
        });
        // eslint-disable-next-line no-underscore-dangle
        self._setEntries(entries);
        return self;
    }
    /**
     * Create a directory from community library item data.
     *
     * @param data The data to create the item from.
     * @param parent The parent of the directory, or `null` for the root.
     * @returns A fresh directory.
     */
    static fromCommunityLibraryData(data, parent) {
        const self = new Directory(null, data.name, Maybe.fromNullable(parent));
        const entries = data.entries.map(entry => expr.match(entry, 'tag', {
            file: file => File.fromCommunityLibraryData(file, self),
            dir: dir => Directory.fromCommunityLibraryData(dir, self),
        }));
        // eslint-disable-next-line no-underscore-dangle
        self._setEntries(entries);
        return self;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromExtractTree(extractTree, parent, dirCls = Directory, fileCls = File) {
        // eslint-disable-next-line new-cap
        const self = new dirCls(null, extractTree.name, Maybe.fromNullable(parent));
        const entries = extractTree.entries.map(entry => {
            if (entry instanceof api.ExtractFileTreeDirectory) {
                return dirCls.fromExtractTree(entry, self, Directory);
            }
            else if (entry instanceof api.ExtractFileTreeFile) {
                return fileCls.fromExtractTree(entry, self);
            }
            else {
                return assert.AssertionError.assertNever(entry);
            }
        });
        // eslint-disable-next-line no-underscore-dangle
        self._setEntries(entries);
        return self;
    }
    /**
     * Create a new `Directory` from a `TarFile`.
     *
     * NOTE: The directories in this tree do not have an id, and ids of the
     * files in this tree are equal to their path in the tar file.
     *
     * @param name The name of the new directory.
     * @param tarFile The `TarFile` to derive the tree from.
     * @returns The file tree representation of the tar archive.
     */
    static fromTarFile(name, tarFile) {
        const root = new Directory(null, name, Maybe.empty());
        // Mapping from path to directory to keep track of which directories have already been seen.
        const dirs = new map.MaybeMap();
        /**
         * Add a directory and all of its parents to the `dirs` map.
         *
         * @param path The path to the directory to be added.
         * @returns The directory pointed to by `path`, along with a list of its children.
         */
        const addDir = (path) => dirs.getOrInsertWith(path.join('/'), () => {
            if (path.length === 0) {
                return { dir: root, children: [] };
            }
            const parent = addDir(path.slice(0, path.length - 1));
            const dir = new Directory(null, path[path.length - 1], Maybe.of(parent.dir));
            parent.children.push(dir);
            return { dir, children: [] };
        });
        tarFile.files.forEach((item, fileId) => {
            const path = item.name.split('/');
            const parent = addDir(path.slice(0, path.length - 1));
            const file = new File(fileId, path[path.length - 1], Maybe.of(parent.dir), Maybe.empty());
            parent.children.push(file);
        });
        dirs.forEach(({ dir, children }) => {
            // eslint-disable-next-line no-underscore-dangle
            dir._setEntries(children);
        });
        return root;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static makeEmpty(id, name, parent) {
        const self = new Directory(id, name, parent);
        // eslint-disable-next-line no-underscore-dangle
        self._setEntries([]);
        return self;
    }
}
__decorate([
    CacheDecorator.cache
], Directory.prototype, "entryLookup", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class AutoTestDirectory extends Directory {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    autoTestSuiteId;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _setEntries(entries) {
        // We don't freeze the object here because we may need to set an
        // additional key later on in _setSuiteId().
        this._entries = Object.freeze(entries);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _setSuiteId(suiteId) {
        if (suiteId != null) {
            this.autoTestSuiteId = suiteId;
        }
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(serverData, parent) {
        const self = Directory.fromServerData(serverData, parent, AutoTestDirectory);
        // eslint-disable-next-line
        self._setSuiteId(serverData.autoTestSuiteId);
        return self;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class DiffEntry extends FileWithParentAndName {
    ids;
    name;
    parent;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isDir = false;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(ids, name, parent) {
        super();
        this.ids = ids;
        this.name = name;
        this.parent = parent;
    }
    get depth() {
        return getDepth(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getRevisions(accum) {
        if (this.ids[0] !== this.ids[1]) {
            accum.set(this.ids[0], this.ids[1]);
            accum.set(this.ids[1], this.ids[0]);
            return true;
        }
        else {
            return false;
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromBaseFiles(file1, file2, parent) {
        // `file1` and `file2` may not both be `Nothing`.
        assert.AssertionError.assert(file1.isJust() || file2.isJust(), 'Must be given at least one file or directory');
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function isDir(maybeDir) {
            return maybeDir.map(f => f instanceof Directory).orDefault(false);
        }
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function isFile(maybeFile) {
            return maybeFile.map(f => f instanceof File).orDefault(false);
        }
        const id1 = file1.map(object.attr('id'));
        const id2 = file2.map(object.attr('id'));
        const name = file1.alt(file2).map(object.attr('name')).unsafeCoerce();
        const ret = [];
        if (isDir(file1) && isDir(file2)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            ret.push(DiffTree.fromDirectories(file1, file2, parent));
        }
        else if (isDir(file1)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            ret.push(DiffTree.fromDirectories(file1, Maybe.empty(), parent));
            if (isFile(file2)) {
                const entry = new DiffEntry([null, id2.unsafeCoerce()], name, parent);
                ret.push(Object.freeze(entry));
            }
        }
        else if (isDir(file2)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            ret.push(DiffTree.fromDirectories(Maybe.empty(), file2, parent));
            if (isFile(file1)) {
                const entry = new DiffEntry([id1.unsafeCoerce(), null], name, parent);
                ret.push(Object.freeze(entry));
            }
        }
        else {
            const ids = [id1.extractNullable(), id2.extractNullable()];
            ret.push(Object.freeze(new DiffEntry(ids, name, parent)));
        }
        return ret;
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], DiffEntry.prototype, "depth", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class DiffTree extends DiffEntry {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isDir = true;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _entries = [];
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get entries() {
        return this._entries;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _setEntries(entries) {
        this._entries = Object.freeze(entries);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromDirectories(dir1, dir2, parent) {
        // `dir1` and `dir2` may not both be `Nothing`.
        assert.AssertionError.assert(dir1.isJust() || dir2.isJust(), 'Must be given at least one directory');
        let merged = dir1.mapOrDefault(object.attr('entries'), []).reduce((accum, cur) => {
            accum[cur.name] = {
                child1: Maybe.of(cur),
                child2: Maybe.empty(),
            };
            return accum;
        }, {});
        merged = dir2.mapOrDefault(object.attr('entries'), []).reduce((accum, cur) => {
            if (!object.hasAttr(accum, cur.name)) {
                accum[cur.name] = {
                    child1: Maybe.empty(),
                    child2: Maybe.of(cur),
                };
            }
            else {
                accum[cur.name].child2 = Maybe.of(cur);
            }
            return accum;
        }, merged);
        const ids = [
            dir1.map(object.attr('id')).extractNullable(),
            dir2.map(object.attr('id')).extractNullable(),
        ];
        const name = dir1.alt(dir2).map(object.attr('name')).unsafeCoerce();
        const self = new DiffTree(ids, name, parent);
        const entries = sort.sortBy(Object.values(merged).reduce((accum, { child1, child2 }) => {
            accum.push(...DiffEntry.fromBaseFiles(child1, child2, Maybe.of(self)));
            return accum;
        }, []), entry => [entry.name, entry instanceof DiffTree], { reversePerKey: [false, true] });
        // eslint-disable-next-line no-underscore-dangle
        self._setEntries(entries);
        return self;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getRevisions(accum) {
        const res = this.entries.reduce((foundAny, entry) => {
            // The method `getRevisions` mutates `accum` so don't short circuit
            // here.
            const val = entry.getRevisions(accum);
            return foundAny || val;
        }, this.ids[0] !== this.ids[1]);
        if (res) {
            if (this.ids[0] === this.ids[1]) {
                accum.set(this.ids[0], null);
            }
            else {
                if (this.ids[0] != null) {
                    accum.set(this.ids[0], this.ids[1]);
                }
                if (this.ids[1] != null) {
                    accum.set(this.ids[1], this.ids[0]);
                }
            }
        }
        return res;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function bfsDir(dir, callback) {
    const queue = new deque.Deque([[dir]]);
    while (queue.length > 0) {
        for (const candidate of queue.shift()) {
            const res = callback(candidate);
            if (res.stop) {
                return Maybe.of(res.result);
            }
            const useFile = res.useFile ?? true;
            if (useFile && (candidate instanceof Directory || candidate instanceof DiffTree)) {
                queue.push(candidate.entries);
            }
        }
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getFirstFileInDir(dir, hiddenOk) {
    return bfsDir(dir, el => {
        if (!el.isDir && (hiddenOk || !el.name.startsWith('.'))) {
            return { stop: true, result: el };
        }
        return { stop: false };
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getFirstMatchingFile(glob, tree, 
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
{ starMatchesHidden }) {
    return bfsDir(tree, candidate => {
        if (candidate instanceof Directory || candidate instanceof DiffTree) {
            if (glob.isDirPattern && glob.match(candidate, { exact: true, starMatchesHidden })) {
                const fileInDir = getFirstFileInDir(candidate, starMatchesHidden);
                if (fileInDir.isJust()) {
                    return { stop: true, result: fileInDir.extract() };
                }
            }
            const useFile = glob.match(candidate, { exact: false, starMatchesHidden });
            return { stop: false, useFile };
        }
        if (glob.match(candidate, { exact: true, starMatchesHidden })) {
            return { stop: true, result: candidate };
        }
        return { stop: false, useFile: false };
    });
}
/**
 * Get the first file in the given tree according to some conditions.
 *
 * 1. Return the first non-hidden files matching the given `glob`.
 * 2. If no such file exists, check the same glob again but this time also against hidden files.
 * 3. If still no match is found the default glob is tried with non-hidden files.
 * 4. Finally the default glob is tried against hidden files.
 *
 * @param glob The glob to check files against.
 * @param tree The directory to search for matching files.
 * @returns `Just` the first file that matches according to the rules listed
 *     above, or `Nothing` if no such file could be found.
 */
export function getFirstFile(glob, tree) {
    for (const usedGlob of [glob.addHead(tree.name), Glob.default()]) {
        for (const starMatchesHidden of [false, true]) {
            const res = getFirstMatchingFile(usedGlob, tree, { starMatchesHidden });
            if (res.isJust()) {
                // Repackage to prevent type error.
                return Maybe.of(res.extract());
            }
        }
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FileTree {
    flattened;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    student;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    teacher;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    diff;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    autotest;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _revisions;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(studentTree, teacherTree, diff, flattened, revisions, autoTestTree = null) {
        this.flattened = flattened;
        this.student = studentTree;
        this.teacher = teacherTree;
        this.diff = diff;
        this.flattened = flattened;
        this._revisions = revisions;
        this.autotest = autoTestTree;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addAutoTestTree(autoTestTree) {
        return new FileTree(this.student, this.teacher, this.diff, this.flattened, this._revisions, AutoTestDirectory.fromServerData(autoTestTree, null));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    removeAutoTestTree() {
        return new FileTree(this.student, this.teacher, this.diff, this.flattened, this._revisions);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(serverStudentTree, serverTeacherTree) {
        const studentTree = Directory.fromServerData(serverStudentTree, null, Directory);
        const teacherTree = serverTeacherTree.map(data => Directory.fromServerData(data, null, Directory));
        const diff = DiffTree.fromDirectories(Maybe.of(studentTree), teacherTree.alt(Maybe.of(studentTree)), Maybe.empty());
        const revisions = new Map();
        if (teacherTree != null) {
            diff.getRevisions(revisions);
        }
        const flattened = Object.freeze(FileTree.flatten(diff));
        return new FileTree(studentTree, teacherTree.extractNullable(), diff, flattened, Object.freeze(revisions));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static flatten(tree, prefix = []) {
        const filePaths = Object.create(null);
        if (tree == null || tree.entries == null) {
            return filePaths;
        }
        tree.entries.forEach(f => {
            prefix.push(f.name);
            if (f instanceof DiffTree) {
                const dirPaths = FileTree.flatten(f, prefix);
                Object.assign(filePaths, dirPaths);
            }
            else {
                const name = prefix.join('/');
                if (f.ids[0] != null) {
                    filePaths[f.ids[0]] = name;
                }
                if (f.ids[1] != null) {
                    filePaths[f.ids[1]] = name;
                }
            }
            prefix.pop();
        });
        return filePaths;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    hasAnyRevision() {
        if (this.teacher == null) {
            return false;
        }
        return this.hasRevision(this.student) || this.hasRevision(this.teacher);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    hasRevision(f) {
        if (f instanceof DiffEntry) {
            return f.ids.some(id => id != null && this.hasRevision(id));
        }
        else {
            let fId;
            if (typeof f === 'string') {
                fId = f;
            }
            else if (object.hasAttr(f, 'id')) {
                fId = f.id;
            }
            else {
                fId = utils.coerceToString(f);
            }
            return this._revisions.has(fId);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getRevisionId(f) {
        return this._revisions.get(f.id);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getFirstFile(glob, revision) {
        return Maybe.fromNullable(this[revision]).chain(tree => getFirstFile(glob, tree));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    searchAnyRevision(id) {
        for (const rev of REVISIONS) {
            const file = this.search(rev, id);
            if (file != null) {
                return file;
            }
        }
        return null;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    search(revision, id) {
        return this.findFileInDir(this[revision], id);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getRevision(id) {
        return REVISIONS.find(rev => this.search(rev, id) != null);
    }
    /**
     * Get a file from the student tree by a path.
     *
     * @param path The path of the file, relative to the root of the student tree.
     * @returns The file if it can be found.
     */
    getByPath(path) {
        let cur = this.student;
        for (const part of path) {
            if (!cur.isDir) {
                return Maybe.empty();
            }
            const next = cur.entryLookup.get(part);
            if (next.isJust()) {
                cur = next.extract();
            }
            else {
                return Maybe.empty();
            }
        }
        if (cur.isDir) {
            return Maybe.empty();
        }
        else {
            return Maybe.of(cur);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    findFileInDir(tree, id, checkRevisions = true) {
        if (tree == null || tree.entries == null || id == null) {
            return null;
        }
        const todo = [tree.entries];
        for (let i = 0; todo.length > i; ++i) {
            const entries = todo[i];
            for (let j = 0; j < entries.length; ++j) {
                const child = entries[j];
                if (child instanceof BaseFile && child.id === id) {
                    return child;
                }
                else if (checkRevisions &&
                    child instanceof BaseFile &&
                    this._revisions.get(child.id) === id) {
                    return child;
                }
                else if (child instanceof DiffEntry &&
                    (child.ids[0] === id || child.ids[1] === id)) {
                    return child;
                }
                else if (child instanceof Directory || child instanceof DiffTree) {
                    todo.push(child.entries);
                }
            }
        }
        return null;
    }
}
